/**
 * PositionMultiSelect.jsx · v2.7.45+E13 (Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Reusable multi-select combobox for the 46-entry Master Position
 * Catalog. Grouped by department (Drivers · Team Leads · Dispatch · …)
 * with a search field on top. Read-only mode renders the selected
 * positions as static chips when the caller is below Partner/Developer
 * tier — preventing self-promotion.
 *
 * Props:
 *   value:    string[] — currently selected positions
 *   onChange: (string[]) => void
 *   canEdit:  bool      — when false, render chips only
 *   placeholder?: string — default "Select positions…"
 *   testidPrefix?: string — for data-testids (default "position-ms")
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  POSITION_CATALOG,
  POSITION_GROUPS,
  POSITION_TO_TIER,
} from "@/lib/positions";
import { Check, ChevronDown, Search, Tags, X, Lock } from "lucide-react";

const TIER_BADGE_COLOR = {
  driver:            "bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300",
  team_leader:       "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  dispatcher:        "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
  supervisor:        "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  operating_partner: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  developer:         "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
  admin:             "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

export default function PositionMultiSelect({
  value = [],
  onChange,
  canEdit = true,
  placeholder = "Select positions…",
  testidPrefix = "position-ms",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = useMemo(() => new Set(value || []), [value]);

  const toggle = (p) => {
    if (!canEdit) return;
    const next = new Set(selected);
    if (next.has(p)) next.delete(p); else next.add(p);
    onChange(Array.from(next));
  };

  const clear = (p) => {
    if (!canEdit) return;
    const next = new Set(selected);
    next.delete(p);
    onChange(Array.from(next));
  };

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return POSITION_GROUPS;
    return POSITION_GROUPS
      .map((g) => ({ ...g, positions: g.positions.filter((p) => p.toLowerCase().includes(term)) }))
      .filter((g) => g.positions.length > 0);
  }, [q]);

  // Read-only mode (caller can't edit) — chips only
  if (!canEdit) {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-readonly`}>
        {(value || []).length === 0 && (
          <div className="text-xs text-slate-500 dark:text-zinc-500 italic flex items-center gap-1.5">
            <Lock className="h-3 w-3"/>No positions assigned · contact a Partner or Developer.
          </div>
        )}
        {(value || []).map((p) => {
          const tier = POSITION_TO_TIER[p] || "driver";
          return (
            <span key={p} className={`text-[11px] px-2 py-0.5 rounded font-semibold ${TIER_BADGE_COLOR[tier] || ""}`}>
              {p}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      {/* Selected chips row */}
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {(value || []).length === 0 && (
          <div className="text-xs text-slate-500 dark:text-zinc-500 italic">No positions selected — click below to add.</div>
        )}
        {(value || []).map((p) => {
          const tier = POSITION_TO_TIER[p] || "driver";
          return (
            <span
              key={p}
              className={`text-[11px] px-2 py-0.5 rounded font-semibold flex items-center gap-1 ${TIER_BADGE_COLOR[tier] || ""}`}
              data-testid={`${testidPrefix}-chip-${p.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {p}
              <button
                type="button"
                onClick={() => clear(p)}
                className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                aria-label={`Remove ${p}`}
              >
                <X className="h-2.5 w-2.5"/>
              </button>
            </span>
          );
        })}
      </div>

      {/* Trigger + popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            className="w-full justify-between text-xs h-9"
            data-testid={`${testidPrefix}-trigger`}
          >
            <span className="flex items-center gap-2 text-slate-600 dark:text-zinc-400">
              <Tags className="h-3.5 w-3.5"/>
              {value?.length > 0 ? `${value.length} position${value.length === 1 ? "" : "s"} selected` : placeholder}
            </span>
            <ChevronDown className="h-3.5 w-3.5"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start" data-testid={`${testidPrefix}-popover`}>
          <div className="border-b border-slate-200 dark:border-zinc-800 p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search positions…"
                className="pl-8 h-8 text-xs"
                data-testid={`${testidPrefix}-search`}
              />
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {groups.length === 0 && (
              <div className="p-3 text-xs text-slate-500 italic">No matches for &quot;{q}&quot;</div>
            )}
            {groups.map((g) => (
              <div key={g.label}>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 bg-slate-50 dark:bg-zinc-900 border-y border-slate-200 dark:border-zinc-800">
                  {g.label}
                </div>
                {g.positions.map((p) => {
                  const isSelected = selected.has(p);
                  const tier = POSITION_TO_TIER[p] || "driver";
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggle(p)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left
                        ${isSelected
                          ? "bg-sky-50 dark:bg-sky-900/20"
                          : "hover:bg-slate-50 dark:hover:bg-zinc-900"}`}
                      data-testid={`${testidPrefix}-opt-${p.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Check className={`h-3.5 w-3.5 flex-shrink-0 ${isSelected ? "text-sky-600" : "text-transparent"}`}/>
                        <span className="truncate">{p}</span>
                      </span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${TIER_BADGE_COLOR[tier] || ""}`}>
                        {tier.replace("_", " ")}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 dark:border-zinc-800 p-2 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500">
              {value?.length || 0} of {POSITION_CATALOG.length} selected
            </div>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs" data-testid={`${testidPrefix}-done`}>
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
